import React, { ReactNode, useCallback, useRef, useState } from 'react'

import { T, Tooltip } from 'govwell-ui'
import { twMerge } from 'tailwind-merge'

export interface ColumnData {
  fieldName: ReactNode
  fieldValue: ReactNode
  key?: string
}

type Props = {
  data: ColumnData[]
  isVertical?: boolean // This component is responsive by default, but can be forced to a vertical layout
}

const getTooltipContent = (node: ReactNode): ReactNode => {
  if (typeof node === 'string' || typeof node === 'number') {
    return node
  }
  return null
}

type TruncatedFieldNameProps = {
  fieldName: ReactNode
}

const TruncatedFieldName = ({ fieldName }: TruncatedFieldNameProps) => {
  const [isTruncated, setIsTruncated] = useState(false)
  const containerRef = useRef<HTMLSpanElement>(null)

  const handleMouseEnter = useCallback(() => {
    if (containerRef.current) {
      setIsTruncated(containerRef.current.scrollWidth > containerRef.current.clientWidth)
    }
  }, [])

  const tooltipContent = getTooltipContent(fieldName)

  return (
    <Tooltip
      content={isTruncated && tooltipContent ? <T.Label>{tooltipContent}</T.Label> : null}
      align="start"
    >
      <span ref={containerRef} className="mr-1 max-w-full truncate" onMouseEnter={handleMouseEnter}>
        <T.Label>{fieldName}</T.Label>
      </span>
    </Tooltip>
  )
}

export const FieldValueTable = ({ data, isVertical = false }: Props) => {
  const renderFieldName = (fieldName: ReactNode, isLast: boolean): ReactNode => (
    <div
      className={twMerge(
        'flex min-w-0 items-center overflow-hidden border-neutral-200 pt-3 pr-0 pb-2',
        isVertical ? '' : `md:pr-4 md:pb-3 ${!isLast ? 'md:border-b' : ''}`
      )}
    >
      <TruncatedFieldName fieldName={fieldName} />
    </div>
  )
  const renderFieldValue = (fieldValue: ReactNode, isLast: boolean): ReactNode => (
    <div
      className={twMerge(
        'flex min-w-0 items-center border-neutral-200 pt-0 pb-3 pl-0',
        isVertical ? '' : `md:pt-3 md:pl-4 ${!isLast ? 'border-b' : ''}`
      )}
    >
      <T.Body className="w-full">{fieldValue}</T.Body>
    </div>
  )

  return (
    <div
      className={twMerge(
        'grid w-full grid-cols-1',
        isVertical ? '' : 'md:grid-cols-[minmax(100px,_30%)_1fr]'
      )}
    >
      {data.map((datum, index, arr) => {
        const isLast = index === arr.length - 1
        return (
          <React.Fragment key={index}>
            {renderFieldName(datum.fieldName, isLast)}
            {renderFieldValue(datum.fieldValue, isLast)}
          </React.Fragment>
        )
      })}
    </div>
  )
}
